import React from 'react'
import AsyncSelect from 'react-select/async';
import Select, { components } from 'react-select';
import '../css/financeiro-parcela-pagar.css';
import { getText } from '../util/translations';

const Placeholder = (props) => {
    return <components.Placeholder {...props} />;
};

// Mensagens padrão do react-select vêm em inglês ("No options"/"Loading...").
// Convidado com idioma carregado recebe a tradução; telas internas caem no literal PT.
const noOptionsMessage = () => getText('semOpcoes') ?? 'Sem opções';
const loadingMessage = () => getText('loading') ?? 'Carregando...';

export default function ReactSelect({
    key = null,
    placeholder = null,
    readOnly = false,
    isDisabled = false,
    isMulti = null,
    id = null,
    name = null,
    onChange = null,
    loadOptions = null,
    options = null,
    value = null,
    className = null,
    defaultOptions = true,
    formatOptionLabel = null,
    isOptionDisabled = null,
    isClearable,
    compact = false,
    // Estilos por parte do react-select ({ control, singleValue, ... }), aplicados DEPOIS dos
    // padrões: cada função recebe o resultado do estilo padrão daquela parte, não o base cru.
    // Filtro do menu (react-select): recebe a opção e o texto digitado. Sem ele, o padrão.
    filterOption = undefined,
    styles = null,
}) {
    const disabled = readOnly || isDisabled;
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;
    const classeRaiz = ['access-react-select', className].filter(Boolean).join(' ');

    // Estilos via API do react-select (mantém o alinhamento vertical interno por construção).
    // `compact` reduz a altura (control menor + indicadores com menos padding) SEM mexer no
    // posicionamento interno — evita o "traço/seta/texto vazando" que acontece ao forçar altura via CSS.
    const customStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: compact ? 34 : 42,
            // fallback literal: o ReactSelect também roda em telas antigas, fora de um ancestral .fin-pp,
            // onde a variável não resolveria
            background: state.isDisabled ? "var(--fin-dis, #e9ecef)" : "#ffffff",
            borderColor: state.isFocused ? "#2563eb" : "#cdd5e0",
            borderRadius: 8,
            boxShadow: state.isFocused ? "0 0 0 3px #eef4ff" : "none",
            fontSize: compact ? '0.85rem' : 14,
            cursor: state.isDisabled ? "not-allowed" : "default",
            pointerEvents: state.isDisabled ? "none" : "auto",
            "&:hover": { borderColor: state.isFocused ? "#2563eb" : "#cdd5e0" },
        }),
        ...(compact ? {
            valueContainer: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
            dropdownIndicator: (base) => ({ ...base, padding: 4 }),
            clearIndicator: (base) => ({ ...base, padding: 4 }),
            input: (base) => ({ ...base, margin: 0, paddingTop: 0, paddingBottom: 0 }),
        } : {}),
        // Acima de qualquer modal (os .fin-pp__modal usam até zIndex 1060) — o menu
        // é renderizado em portal no <body>, então só existe enquanto aberto.
        menu: (base) => ({
            ...base,
            zIndex: 2000,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 2000,
        }),
    };
    const estilos = { ...customStyles };
    Object.entries(styles || {}).forEach(([parte, fn]) => {
        const padrao = customStyles[parte];
        estilos[parte] = (base, state) => fn(padrao ? padrao(base, state) : base, state);
    });

    if (options) {//opções fixas
        return <Select
            isClearable={isClearable}
            isMulti={isMulti}
            placeholder={placeholder}
            noOptionsMessage={noOptionsMessage}
            loadingMessage={loadingMessage}
            components={{ Placeholder }}
            styles={estilos}
            menuPortalTarget={menuPortalTarget}
            key={key}
            id={id}
            isDisabled={disabled}
            name={name}
            value={isMulti ? value : (value && value.value ? options.find((option) => option.value === value.value) : null)}
            onChange={onChange}
            options={options}
            isOptionDisabled={(option) => option.disabled === true || (typeof isOptionDisabled === 'function' && isOptionDisabled(option))}
            filterOption={filterOption}
            className={classeRaiz}
            formatOptionLabel={formatOptionLabel}
        />
    } else {//opções via api
        return <AsyncSelect
            isClearable={isClearable}
            isMulti={isMulti}
            placeholder={placeholder}
            noOptionsMessage={noOptionsMessage}
            loadingMessage={loadingMessage}
            components={{ Placeholder }}
            styles={estilos}
            menuPortalTarget={menuPortalTarget}
            key={key}
            id={id}
            isDisabled={disabled}
            name={name}
            cacheOptions
            defaultOptions={defaultOptions}
            value={value}
            onChange={onChange}
            loadOptions={loadOptions}
            isOptionDisabled={(option) => option.disabled === true || (typeof isOptionDisabled === 'function' && isOptionDisabled(option))}
            className={classeRaiz}
            formatOptionLabel={formatOptionLabel}
        />
    }
}
